import { useQuery, QueryStatus } from "@tanstack/react-query";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { BrandGovernanceApiFactory, type LiveMonitorParams } from "@/apis/brand-os/brand-governance";
import type { LiveMonitorData, LiveMonitorMonitor } from "@/schema/brand-os/brand-governance";

function statsMonitor(monitor: LiveMonitorMonitor) {
  return [
    { label: "Total", value: monitor.total, valueClassName: "text-foreground" },
    { label: "Good", value: monitor.good, valueClassName: "text-success" },
    { label: "Requires Review", value: monitor.requires_review, valueClassName: "text-warning" },
    { label: "Flagged for Improvement", value: monitor.flagged_for_improvement, valueClassName: "text-destructive" },
  ];
}

function StatsContent({ data }: { data: LiveMonitorData }) {
  const stats = data.monitor ? statsMonitor(data.monitor) : [];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {stats.map(({ label, value, valueClassName }) => (
        <Card key={label}>
          <CardContent className="flex flex-col gap-1 px-5">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn("text-3xl font-bold", valueClassName)}>{value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function StatsComponent({ status, data }: { status: QueryStatus; data?: LiveMonitorData | null }) {
  switch (status) {
    case "pending":
      return <StatsSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return data ? <StatsContent data={data} /> : <LockedContent message={message} />;
  }
}

export function Stats({ params }: { params: LiveMonitorParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.LiveMonitor(params));

  return <StatsComponent status={status} data={data?.data} />;
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i}>
          <CardContent className="flex flex-col gap-1 px-5">
            <Skeleton className="h-4 w-24 rounded-md" />
            <Skeleton className="h-9 w-12 rounded-md" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
